import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Pencil, Trash2, Upload, Sparkles, GripVertical, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

type GalleryImage = {
  id: string;
  created_at: string;
  image_url: string;
  caption: string | null;
  alt_text: string;
  publish: boolean;
  display_order: number;
};

type SortableImageProps = {
  image: GalleryImage;
  onEdit: (image: GalleryImage) => void;
  onDelete: (id: string) => void;
};

const SortableImage = ({ image, onEdit, onDelete }: SortableImageProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: image.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : "auto",
  };

  return (
    <div ref={setNodeRef} style={style} className="relative group">
      <div className={`aspect-square rounded-lg overflow-hidden border border-border ${isDragging ? "ring-2 ring-primary shadow-lg" : ""}`}>
        <img
          src={image.image_url}
          alt={image.alt_text}
          className="w-full h-full object-cover"
        />
      </div>
      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
        <Button
          variant="secondary"
          size="icon"
          className="cursor-grab active:cursor-grabbing"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="w-4 h-4" />
        </Button>
        <Button variant="secondary" size="icon" onClick={() => onEdit(image)}>
          <Pencil className="w-4 h-4" />
        </Button>
        <Button variant="secondary" size="icon" onClick={() => onDelete(image.id)}>
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
      {!image.publish && (
        <div className="absolute top-2 right-2 bg-destructive text-destructive-foreground text-xs px-2 py-1 rounded">
          Unpublished
        </div>
      )}
      <div className="mt-2 text-xs text-muted-foreground">
        Order: {image.display_order}
      </div>
    </div>
  );
};

const Gallery = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [images, setImages] = useState<GalleryImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingImage, setEditingImage] = useState<GalleryImage | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [generatingAltText, setGeneratingAltText] = useState(false);
  const [generatingCaption, setGeneratingCaption] = useState(false);

  const [formData, setFormData] = useState({
    image_url: "",
    caption: "",
    alt_text: "",
    publish: true,
    display_order: 0,
  });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchImages();
  }, []);

  const fetchImages = async () => {
    try {
      const { data, error } = await supabase
        .from("gallery_images")
        .select("*")
        .order("display_order", { ascending: true });

      if (error) throw error;
      setImages(data || []);
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Failed to fetch gallery images",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (image?: GalleryImage) => {
    if (image) {
      setEditingImage(image);
      setFormData({
        image_url: image.image_url,
        caption: image.caption || "",
        alt_text: image.alt_text,
        publish: image.publish,
        display_order: image.display_order,
      });
    } else {
      setEditingImage(null);
      setFormData({
        image_url: "",
        caption: "",
        alt_text: "",
        publish: true,
        display_order: images.length,
      });
    }
    setIsDialogOpen(true);
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast({
        title: "Error",
        description: "Please upload an image file",
        variant: "destructive",
      });
      return;
    }

    setUploading(true);
    try {
      const fileExt = file.name.split(".").pop();
      const fileName = `${Math.random()}.${fileExt}`;
      const filePath = `${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from("gallery-images")
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data } = supabase.storage.from("gallery-images").getPublicUrl(filePath);
      
      setFormData({ ...formData, image_url: data.publicUrl });
      toast({
        title: "Success",
        description: "Image uploaded successfully",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Failed to upload image",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const generateWithAI = async (type: "alt_text" | "caption") => {
    if (!formData.image_url) {
      toast({
        title: "Error",
        description: "Please upload an image first",
        variant: "destructive",
      });
      return;
    }

    if (type === "alt_text") {
      setGeneratingAltText(true);
    } else {
      setGeneratingCaption(true);
    }

    try {
      const { data, error } = await supabase.functions.invoke("analyze-image", {
        body: { imageUrl: formData.image_url, type },
      });

      if (error) throw error;

      if (data.error) {
        throw new Error(data.error);
      }

      const generatedText = type === "alt_text" ? data.alt_text : data.caption;
      
      if (generatedText) {
        setFormData({ ...formData, [type]: generatedText });
        toast({
          title: "Success",
          description: `${type === "alt_text" ? "Alt text" : "Caption"} generated successfully`,
        });
      } else {
        throw new Error("No text generated");
      }
    } catch (error: any) {
      console.error("AI generation error:", error);
      toast({
        title: "Error",
        description: error.message || `Failed to generate ${type === "alt_text" ? "alt text" : "caption"}`,
        variant: "destructive",
      });
    } finally {
      if (type === "alt_text") {
        setGeneratingAltText(false);
      } else {
        setGeneratingCaption(false);
      }
    }
  };

  const handleSubmit = async () => {
    if (!formData.image_url || !formData.alt_text) {
      toast({
        title: "Error",
        description: "Image and alt text are required",
        variant: "destructive",
      });
      return;
    }

    try {
      if (editingImage) {
        const { error } = await supabase
          .from("gallery_images")
          .update(formData)
          .eq("id", editingImage.id);

        if (error) throw error;
        toast({
          title: "Success",
          description: "Gallery image updated successfully",
        });
      } else {
        const { error } = await supabase.from("gallery_images").insert(formData);

        if (error) throw error;
        toast({
          title: "Success",
          description: "Gallery image added successfully",
        });
      }

      setIsDialogOpen(false);
      fetchImages();
    } catch (error: any) {
      toast({
        title: "Error",
        description: editingImage ? "Failed to update image" : "Failed to add image",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    try {
      const { error } = await supabase.from("gallery_images").delete().eq("id", deleteId);

      if (error) throw error;
      toast({
        title: "Success",
        description: "Gallery image deleted successfully",
      });
      fetchImages();
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Failed to delete image",
        variant: "destructive",
      });
    } finally {
      setDeleteId(null);
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = images.findIndex((img) => img.id === active.id);
    const newIndex = images.findIndex((img) => img.id === over.id);

    const newImages = arrayMove(images, oldIndex, newIndex);
    
    // Optimistically update UI
    setImages(newImages);

    // Prepare batch updates
    const updates = newImages.map((img, index) => ({
      id: img.id,
      display_order: index,
    }));

    try {
      // Update all affected images in the database
      for (const update of updates) {
        const { error } = await supabase
          .from("gallery_images")
          .update({ display_order: update.display_order })
          .eq("id", update.id);

        if (error) throw error;
      }

      toast({
        title: "Success",
        description: "Image order updated",
      });
    } catch (error: any) {
      console.error("Reorder error:", error);
      toast({
        title: "Error",
        description: "Failed to update order",
        variant: "destructive",
      });
      // Revert on error
      fetchImages();
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/admin/dashboard")}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-xl font-bold text-foreground">Manage Gallery</h1>
          </div>
          <Button onClick={() => handleOpenDialog()}>
            <Plus className="w-4 h-4 mr-2" />
            Add Image
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <Card>
          <CardHeader>
            <CardTitle>Gallery Images</CardTitle>
            <CardDescription>
              Manage project photos and portfolio images. Drag and drop to reorder.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-muted-foreground">Loading...</div>
            ) : images.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">No images found</div>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext items={images.map(img => img.id)} strategy={rectSortingStrategy}>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {images.map((image) => (
                      <SortableImage
                        key={image.id}
                        image={image}
                        onEdit={handleOpenDialog}
                        onDelete={setDeleteId}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingImage ? "Edit Image" : "Add Image"}</DialogTitle>
            <DialogDescription>
              {editingImage ? "Update gallery image information" : "Add a new image to the gallery"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="image_file">Upload Image</Label>
              <div className="flex items-center gap-4">
                <Input
                  id="image_file"
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  disabled={uploading}
                />
                {uploading && <span className="text-sm text-muted-foreground">Uploading...</span>}
              </div>
              {formData.image_url && (
                <img src={formData.image_url} alt="Preview" className="mt-2 w-full max-h-48 object-cover rounded-lg" />
              )}
            </div>
            
            <div>
              <Label htmlFor="alt_text">Alt Text (Required for SEO)</Label>
              <div className="flex gap-2">
                <Input
                  id="alt_text"
                  value={formData.alt_text}
                  onChange={(e) => setFormData({ ...formData, alt_text: e.target.value })}
                  placeholder="Describe the image for accessibility"
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => generateWithAI("alt_text")}
                  disabled={!formData.image_url || generatingAltText}
                  className="whitespace-nowrap"
                >
                  {generatingAltText ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4 mr-2" />
                  )}
                  Generate
                </Button>
              </div>
              {!formData.image_url && (
                <p className="text-xs text-muted-foreground mt-1">Upload an image to enable AI generation</p>
              )}
            </div>
            
            <div>
              <Label htmlFor="caption">Caption (Optional)</Label>
              <div className="flex gap-2">
                <Textarea
                  id="caption"
                  value={formData.caption}
                  onChange={(e) => setFormData({ ...formData, caption: e.target.value })}
                  rows={2}
                  placeholder="Optional caption to display with the image"
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => generateWithAI("caption")}
                  disabled={!formData.image_url || generatingCaption}
                  className="whitespace-nowrap self-start"
                >
                  {generatingCaption ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4 mr-2" />
                  )}
                  Generate
                </Button>
              </div>
            </div>
            
            <div>
              <Label htmlFor="display_order">Display Order</Label>
              <Input
                id="display_order"
                type="number"
                value={formData.display_order}
                onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) })}
              />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="publish">Publish</Label>
              <Switch
                id="publish"
                checked={formData.publish}
                onCheckedChange={(checked) => setFormData({ ...formData, publish: checked })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={uploading || generatingAltText || generatingCaption}>
              {editingImage ? "Update" : "Add"} Image
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this image. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Gallery;
